import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Calendar, Views, dateFnsLocalizer } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './CalendarAgenda.css';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { toast } from 'sonner';
import {
  Search, Video, Copy, Phone, Mail, Cake, Calendar as CalIcon, Clock,
  User, CreditCard, ExternalLink, Check, XCircle, CalendarPlus, UserPlus,
  MessageCircle, CalendarCheck, FileText, Edit, ChevronLeft, ChevronRight, Save, X,
  Trash2, AlertTriangle
} from 'lucide-react';
import { formatPhoneBR, normalizePhone } from '../../lib/phone';
import { addDays, addWeeks, addMonths, subDays, subWeeks, subMonths } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

const locales = { 'pt-BR': ptBR };
const localizer = dateFnsLocalizer({ format, parse, startOfWeek: (d) => startOfWeek(d, { locale: ptBR }), getDay, locales });
const DnDCalendar = withDragAndDrop(Calendar);

const messages = {
  date: 'Data', time: 'Hora', event: 'Consulta',
  allDay: 'Dia inteiro', week: 'Semana', work_week: 'Semana útil',
  day: 'Dia', month: 'Mês', previous: 'Anterior', next: 'Próximo',
  yesterday: 'Ontem', tomorrow: 'Amanhã', today: 'Hoje', agenda: 'Agenda',
  noEventsInRange: 'Nenhuma consulta no período.',
  showMore: (count) => `+${count} mais`,
};

const STATUS_STYLE = {
  scheduled:        { bg: '#0F1B3D', border: '#0F1B3D', label: 'Confirmada' },
  rescheduled:      { bg: '#0F1B3D', border: '#0F1B3D', label: 'Reagendada' },
  pending_approval: { bg: '#B8845C', border: '#8E5F3D', label: 'Aguardando aprovação' },
  awaiting_payment: { bg: '#E9C46A', border: '#C49B3F', label: 'Aguard. pagamento', dark: true },
  completed:        { bg: '#5F6B85', border: '#3B4360', label: 'Concluída' },
  cancelled:        { bg: '#A8174E', border: '#8E1142', label: 'Cancelada' },
  rejected:         { bg: '#A8174E', border: '#8E1142', label: 'Rejeitada' },
  no_show:          { bg: '#A8174E', border: '#8E1142', label: 'Faltou' },
};

const TYPE_LABEL = { clinica_geral: 'Clínica Geral', consulta_geral: 'Clínica Geral', tirzepatida: 'Tirzepatida', retorno: 'Retorno' };

const DRAGGABLE_STATUSES = new Set(['scheduled', 'rescheduled', 'pending_approval', 'awaiting_payment']);

const buildEvent = (apt, patientById) => {
  const [h, m] = (apt.time || '00:00').split(':').map(Number);
  const [y, mo, d] = (apt.date || '').split('-').map(Number);
  if (!y || !mo || !d) return null;
  const start = new Date(y, mo - 1, d, h || 0, m || 0);
  const duration = apt.duration_minutes || 30;
  const end = new Date(start.getTime() + duration * 60 * 1000);
  const patient = patientById[apt.patient_id] || {};
  return { id: apt.appointment_id, title: patient.name || 'Paciente', start, end, resource: { ...apt, patient } };
};

const EventBlock = ({ event }) => {
  const apt = event.resource;
  const s = STATUS_STYLE[apt.status] || STATUS_STYLE.scheduled;
  const isPaid = apt.payment_status === 'paid';
  const p = apt.patient || {};
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex flex-col leading-tight overflow-hidden h-full">
            <div className="flex items-center justify-between gap-1">
              <span className="truncate font-semibold">{event.title}</span>
              <span className="opacity-90 shrink-0" title={isPaid ? 'Pago' : 'Não pago'}>{isPaid ? '●' : '○'}</span>
            </div>
            <div className="text-[10px] opacity-90 truncate">
              {apt.time} · {TYPE_LABEL[apt.type] || apt.type}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <p className="font-semibold">{p.name || 'Paciente'}</p>
          <p className="text-xs">Telefone: {p.phone || '—'}</p>
          <p className="text-xs">Horário: {apt.time} · {s.label}</p>
          <p className="text-xs">Pagamento: {isPaid ? 'Pago' : 'Pendente'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const CalendarAgenda = () => {
  const [appointments, setAppointments] = useState([]);
  const [patients, setPatients] = useState([]);
  const [servicePrices, setServicePrices] = useState([]);
  const [view, setView] = useState(Views.WEEK);
  const [date, setDate] = useState(new Date());
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedApt, setSelectedApt] = useState(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [form, setForm] = useState({ patient_id: '', date: '', time: '', type: 'clinica_geral', notes: '' });

  // Patient combobox + inline create (same UX as AppointmentsManagement)
  const [patientSearch, setPatientSearch] = useState('');
  const [patientListOpen, setPatientListOpen] = useState(false);
  const [inlineNewOpen, setInlineNewOpen] = useState(false);
  const [inlineNew, setInlineNew] = useState({ name: '', phone: '', email: '' });
  const [creatingInline, setCreatingInline] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [a, p, sp] = await Promise.all([
        axios.get(`${API_URL}/api/appointments`, { withCredentials: true }),
        axios.get(`${API_URL}/api/patients`, { withCredentials: true }),
        axios.get(`${API_URL}/api/prices`, { withCredentials: true })
      ]);
      setAppointments(a.data || []);
      setPatients(p.data || []);
      setServicePrices(sp.data || []);
    } catch (e) {
      toast.error('Erro ao carregar agenda');
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const patientById = useMemo(() => Object.fromEntries(patients.map(p => [p.patient_id, p])), [patients]);

  const events = useMemo(() => {
    const term = search.trim().toLowerCase();
    return appointments
      .filter(a => {
        if (statusFilter !== 'all' && a.status !== statusFilter) return false;
        if (!term) return true;
        const p = patientById[a.patient_id] || {};
        return (
          (p.name || '').toLowerCase().includes(term) ||
          (p.phone || '').includes(term) ||
          (p.email || '').toLowerCase().includes(term)
        );
      })
      .map(a => buildEvent(a, patientById))
      .filter(Boolean);
  }, [appointments, patientById, search, statusFilter]);

  const eventPropGetter = (event) => {
    const s = STATUS_STYLE[event.resource.status] || STATUS_STYLE.scheduled;
    return {
      style: {
        backgroundColor: s.bg, borderColor: s.border,
        color: s.dark ? '#0F1B3D' : '#FAF6EE',
        borderRadius: 8, border: `1px solid ${s.border}`,
        padding: '2px 6px', fontSize: 12, fontWeight: 500,
        boxShadow: '0 1px 2px rgba(15,27,61,0.08)',
      }
    };
  };

  const handleMove = async ({ event, start }) => {
    const apt = event.resource;
    if (!DRAGGABLE_STATUSES.has(apt.status)) {
      toast.error('Esta consulta não pode ser movida.');
      return;
    }
    const yyyy = start.getFullYear();
    const mm = String(start.getMonth() + 1).padStart(2, '0');
    const dd = String(start.getDate()).padStart(2, '0');
    const hh = String(start.getHours()).padStart(2, '0');
    const mi = String(start.getMinutes()).padStart(2, '0');
    const newDate = `${yyyy}-${mm}-${dd}`;
    const newTime = `${hh}:${mi}`;
    const original = appointments;
    setAppointments(prev => prev.map(a => a.appointment_id === apt.appointment_id ? { ...a, date: newDate, time: newTime } : a));
    try {
      await axios.put(`${API_URL}/api/appointments/${apt.appointment_id}`, { date: newDate, time: newTime }, { withCredentials: true });
      toast.success(`Movida para ${new Date(`${newDate}T${newTime}`).toLocaleString('pt-BR')}`);
    } catch (e) {
      toast.error('Erro ao mover. Restaurando.');
      setAppointments(original);
    }
  };

  const onSelectEvent = (event) => setSelectedApt(event.resource);
  const eventComponents = useMemo(() => ({ event: EventBlock }), []);
  const goToday = () => setDate(new Date());

  const navigatePrev = () => {
    if (view === Views.DAY) setDate(subDays(date, 1));
    else if (view === Views.WEEK) setDate(subWeeks(date, 1));
    else if (view === Views.MONTH) setDate(subMonths(date, 1));
  };
  const navigateNext = () => {
    if (view === Views.DAY) setDate(addDays(date, 1));
    else if (view === Views.WEEK) setDate(addWeeks(date, 1));
    else if (view === Views.MONTH) setDate(addMonths(date, 1));
  };

  const currentLabel = useMemo(() => {
    if (view === Views.MONTH) {
      return date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    }
    if (view === Views.WEEK) {
      const start = startOfWeek(date, { locale: ptBR });
      const end = addDays(start, 6);
      const sameMonth = start.getMonth() === end.getMonth();
      const sm = start.toLocaleDateString('pt-BR', { day: '2-digit', month: sameMonth ? undefined : 'short' });
      const em = end.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
      return `${sm} – ${em}`;
    }
    return date.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  }, [view, date]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/appointments`, form, { withCredentials: true });
      toast.success('Consulta agendada!');
      setIsCreateOpen(false);
      setForm({ patient_id: '', date: '', time: '', type: 'clinica_geral', notes: '' });
      setPatientSearch('');
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Erro'); }
  };

  const handleInlineCreatePatient = async (e) => {
    e.preventDefault();
    if (!inlineNew.name.trim() || !inlineNew.phone.trim()) { toast.error('Nome e celular obrigatórios'); return; }
    setCreatingInline(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/bulk-register-patients`, {
        default_password: '1234',
        patients: [{ name: inlineNew.name.trim(), phone: normalizePhone(inlineNew.phone), email: inlineNew.email.trim() || null }],
      }, { withCredentials: true });
      const res = data?.results?.[0];
      if (res?.status === 'created' || res?.status === 'skipped') {
        const pr = await axios.get(`${API_URL}/api/patients`, { withCredentials: true });
        setPatients(pr.data);
        const norm = normalizePhone(inlineNew.phone);
        const picked = pr.data.find(p => p.phone === norm || p.phone_normalized === norm);
        if (picked) setForm(prev => ({ ...prev, patient_id: picked.patient_id }));
        toast.success(res.status === 'created' ? 'Paciente cadastrado! Senha padrão: 1234' : 'Paciente já cadastrado — selecionado');
        setInlineNewOpen(false);
        setInlineNew({ name: '', phone: '', email: '' });
        setPatientSearch('');
      } else {
        toast.error(res?.reason || 'Erro ao cadastrar');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao cadastrar');
    }
    setCreatingInline(false);
  };

  return (
    <div className="space-y-4" data-testid="calendar-agenda">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-3xl font-medium text-[#0F1B3D]" style={{ fontFamily: 'Outfit, sans-serif' }}>Consultas</h2>
          <p className="text-sm text-[#5F6B85]">Visualize, busque e reorganize sua agenda</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={navigatePrev} className="h-9 w-9 border-[#E5DCC8]" data-testid="cal-prev-btn" aria-label="Anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToday} className="border-[#E5DCC8] h-9" data-testid="cal-today-btn">Hoje</Button>
            <Button variant="outline" size="icon" onClick={navigateNext} className="h-9 w-9 border-[#E5DCC8]" data-testid="cal-next-btn" aria-label="Próximo">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <span className="text-sm text-[#0F1B3D] font-medium capitalize px-2 hidden md:inline" data-testid="cal-current-label">{currentLabel}</span>
          <div className="flex rounded-lg border border-[#E5DCC8] overflow-hidden">
            {[{ v: Views.DAY, l: 'Dia' }, { v: Views.WEEK, l: 'Semana' }, { v: Views.MONTH, l: 'Mês' }].map(opt => (
              <button
                key={opt.v}
                onClick={() => setView(opt.v)}
                className={`px-3 py-1.5 text-sm transition-colors ${view === opt.v ? 'bg-[#0F1B3D] text-white' : 'bg-white text-[#5F6B85] hover:bg-[#FAF6EE]'}`}
                data-testid={`cal-view-${opt.l.toLowerCase()}`}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="new-appointment-button">
                <CalendarPlus className="h-4 w-4 mr-2" /> Nova Consulta
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle>Agendar Consulta</DialogTitle></DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4">
                <div className="space-y-2">
                  <Label>Paciente *</Label>
                  {(() => {
                    const selectedPatient = patients.find(p => p.patient_id === form.patient_id);
                    const q = patientSearch.trim().toLowerCase();
                    const filteredPatients = patients
                      .filter(p => p.status === 'active')
                      .filter(p => !q || (p.name || '').toLowerCase().includes(q) || (p.phone || '').includes(q) || (p.email || '').toLowerCase().includes(q))
                      .slice(0, 25);
                    return (
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A85A0]" />
                          <Input
                            value={selectedPatient ? selectedPatient.name : patientSearch}
                            onChange={e => { setForm({...form, patient_id: ''}); setPatientSearch(e.target.value); setPatientListOpen(true); }}
                            onFocus={() => setPatientListOpen(true)}
                            placeholder="Buscar por nome, telefone ou e-mail..."
                            className="pl-9"
                            data-testid="cal-appointment-patient-search"
                          />
                        </div>
                        {patientListOpen && (
                          <div className="absolute z-30 mt-1 w-full max-h-64 overflow-auto bg-white border border-[#E5DCC8] rounded-lg shadow-lg">
                            {filteredPatients.length > 0 ? filteredPatients.map(p => (
                              <button
                                key={p.patient_id}
                                type="button"
                                className="w-full text-left px-3 py-2 hover:bg-[#FAF6EE] flex flex-col border-b border-[#FAF6EE] last:border-0"
                                onClick={() => { setForm({...form, patient_id: p.patient_id}); setPatientSearch(''); setPatientListOpen(false); }}
                                data-testid={`cal-patient-option-${p.patient_id}`}
                              >
                                <span className="text-sm font-medium text-[#0F1B3D]">{p.name}</span>
                                <span className="text-xs text-[#7A85A0]">{formatPhoneBR(p.phone) || '—'}{p.email ? ` · ${p.email}` : ''}</span>
                              </button>
                            )) : (
                              <div className="px-3 py-4 text-center text-sm text-[#7A85A0]">Nenhum paciente encontrado</div>
                            )}
                            <button
                              type="button"
                              className="w-full text-left px-3 py-2 bg-[#FAF6EE] hover:bg-[#F3EAD5] text-sm font-medium text-[#A8174E] border-t border-[#E5DCC8] flex items-center gap-2"
                              onClick={() => { setInlineNew({ name: patientSearch, phone: '', email: '' }); setInlineNewOpen(true); setPatientListOpen(false); }}
                              data-testid="cal-new-patient-inline-btn"
                            >
                              <UserPlus className="h-4 w-4" /> Cadastrar novo paciente
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Data *</Label><Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} required data-testid="appointment-date-input" /></div>
                  <div className="space-y-2"><Label>Horário *</Label><Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} required data-testid="appointment-time-input" /></div>
                </div>
                <div className="space-y-2">
                  <Label>Tipo *</Label>
                  <Select value={form.type} onValueChange={val => setForm({...form, type: val})}>
                    <SelectTrigger data-testid="appointment-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {servicePrices.map(sp => <SelectItem key={sp.service_type} value={sp.service_type}>{sp.label} - R$ {sp.price.toFixed(2)}</SelectItem>)}
                      {servicePrices.length === 0 && <SelectItem value="clinica_geral">Clínica Geral</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Observações</Label><Input value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} data-testid="appointment-notes-input" /></div>
                <Button type="submit" className="w-full bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="appointment-submit-button">Agendar</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7A85A0]" />
          <Input placeholder="Buscar por nome, telefone ou email..." className="pl-10" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="cal-search-input" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="cal-status-filter"><SelectValue placeholder="Status da Consulta" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="pending_approval">Aguardando aprovação</SelectItem>
            <SelectItem value="awaiting_payment">Aguard. pagamento</SelectItem>
            <SelectItem value="scheduled">Confirmada</SelectItem>
            <SelectItem value="completed">Concluída</SelectItem>
            <SelectItem value="cancelled">Cancelada</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-[#5F6B85]">
        {[
          { c: '#0F1B3D', l: 'Confirmada' },
          { c: '#B8845C', l: 'Aguard. aprovação' },
          { c: '#E9C46A', l: 'Aguard. pagamento' },
          { c: '#5F6B85', l: 'Concluída' },
          { c: '#A8174E', l: 'Cancelada' },
        ].map(it => (
          <div key={it.l} className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: it.c }} />{it.l}
          </div>
        ))}
        <span className="ml-auto opacity-70">● Pago · ○ Não pago</span>
      </div>

      {/* Calendar */}
      <div className="bg-white rounded-2xl border border-[#E5DCC8] p-2 sm:p-4" style={{ height: '70vh' }}>
        <DnDCalendar
          localizer={localizer}
          messages={messages}
          culture="pt-BR"
          events={events}
          view={view}
          onView={setView}
          date={date}
          onNavigate={setDate}
          views={[Views.DAY, Views.WEEK, Views.MONTH]}
          step={30}
          timeslots={2}
          min={new Date(2000, 0, 1, 7, 0)}
          max={new Date(2000, 0, 1, 21, 0)}
          eventPropGetter={eventPropGetter}
          components={eventComponents}
          onEventDrop={handleMove}
          resizable={false}
          draggableAccessor={(e) => DRAGGABLE_STATUSES.has(e.resource.status)}
          onSelectEvent={onSelectEvent}
          selectable
          onSelectSlot={(slotInfo) => {
            const d = slotInfo.start;
            const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
            const t = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
            setForm(prev => ({ ...prev, date: iso, time: t }));
            setIsCreateOpen(true);
          }}
          popup
        />
      </div>

      {/* Side drawer */}
      <AppointmentDrawer
        apt={selectedApt}
        onClose={() => setSelectedApt(null)}
        onUpdated={() => { fetchData(); setSelectedApt(null); }}
      />

      {/* Inline Create Patient Dialog */}
      <Dialog open={inlineNewOpen} onOpenChange={setInlineNewOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-[#A8174E]" /> Cadastrar novo paciente
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleInlineCreatePatient} className="space-y-3">
            <div className="space-y-2">
              <Label>Nome completo *</Label>
              <Input value={inlineNew.name} onChange={e => setInlineNew({...inlineNew, name: e.target.value})} required data-testid="cal-inline-new-name" />
            </div>
            <div className="space-y-2">
              <Label>Celular (WhatsApp) *</Label>
              <Input value={inlineNew.phone} onChange={e => setInlineNew({...inlineNew, phone: formatPhoneBR(e.target.value)})} placeholder="(00) 00000-0000" inputMode="numeric" maxLength={15} required data-testid="cal-inline-new-phone" />
            </div>
            <div className="space-y-2">
              <Label>E-mail (opcional)</Label>
              <Input type="email" value={inlineNew.email} onChange={e => setInlineNew({...inlineNew, email: e.target.value})} placeholder="paciente@email.com" data-testid="cal-inline-new-email" />
            </div>
            <div className="rounded-lg bg-[#FAF6EE] border border-[#E5DCC8] p-3 text-xs text-[#5F6B85]">
              <strong className="text-[#0F1B3D]">Senha padrão:</strong> <code className="text-[#A8174E]">1234</code>
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={creatingInline} className="flex-1 bg-[#A8174E] hover:bg-[#8E1142] text-white" data-testid="cal-inline-new-submit">
                {creatingInline ? 'Cadastrando...' : 'Cadastrar e selecionar'}
              </Button>
              <Button type="button" variant="outline" className="border-[#E5DCC8]" onClick={() => setInlineNewOpen(false)}>Cancelar</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const AppointmentDrawer = ({ apt, onClose, onUpdated }) => {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({ date: '', time: '', type: '' });
  const [servicePrices, setServicePrices] = useState([]);
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  useEffect(() => {
    if (apt) {
      setEditForm({ date: apt.date || '', time: apt.time || '', type: apt.type || '' });
      setEditing(false);
      setShowDeleteWarning(false);
      setDeleteConfirmText('');
    }
  }, [apt]);

  useEffect(() => {
    axios.get(`${API_URL}/api/prices`, { withCredentials: true })
      .then(r => setServicePrices(r.data || []))
      .catch(() => {});
  }, []);

  if (!apt) return null;
  const p = apt.patient || {};
  const s = STATUS_STYLE[apt.status] || STATUS_STYLE.scheduled;
  const isPaid = apt.payment_status === 'paid';
  const isLocked = ['completed', 'cancelled', 'rejected', 'no_show'].includes(apt.status);
  const fullDate = (() => {
    try {
      const [y, m, d] = apt.date.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    } catch { return apt.date; }
  })();

  const callApi = async (fn, successMsg) => {
    setBusy(true);
    try { await fn(); toast.success(successMsg); onUpdated(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
    setBusy(false);
  };

  const saveEdit = async () => {
    const payload = {};
    if (editForm.date && editForm.date !== apt.date) payload.date = editForm.date;
    if (editForm.time && editForm.time !== apt.time) payload.time = editForm.time;
    if (editForm.type && editForm.type !== apt.type) payload.type = editForm.type;
    if (Object.keys(payload).length === 0) { setEditing(false); return; }
    await callApi(
      () => axios.put(`${API_URL}/api/appointments/${apt.appointment_id}`, payload, { withCredentials: true }),
      'Consulta atualizada'
    );
    setEditing(false);
  };

  const approve = () => callApi(
    () => axios.post(`${API_URL}/api/appointments/${apt.appointment_id}/approve`, {}, { withCredentials: true }),
    'Aprovada! Paciente notificado.'
  );
  const reject = () => {
    if (!window.confirm('Rejeitar esta solicitação?')) return;
    callApi(
      () => axios.post(`${API_URL}/api/appointments/${apt.appointment_id}/reject`, {}, { withCredentials: true }),
      'Rejeitada'
    );
  };
  const markPaid = () => {
    if (!window.confirm('Marcar pagamento como Pago? Isso gerará o link do Google Meet.')) return;
    callApi(
      () => axios.post(`${API_URL}/api/pix/confirm/${apt.appointment_id}`, {}, { withCredentials: true }),
      'Pagamento confirmado!'
    );
  };
  const setStatus = (status) => callApi(
    () => axios.put(`${API_URL}/api/appointments/${apt.appointment_id}`, { status }, { withCredentials: true }),
    'Status atualizado'
  );
  const generateMeet = () => callApi(
    () => axios.post(`${API_URL}/api/calendar/events`, {
      appointment_id: apt.appointment_id, patient_id: apt.patient_id,
      title: `Consulta - ${p.name}`, date: apt.date, time: apt.time, duration_minutes: 30
    }, { withCredentials: true }),
    'Evento criado no Google Calendar!'
  );

  const sendWhatsApp = () => {
    const phone = (p.phone || '').replace(/\D/g, '');
    const full = phone.startsWith('55') ? phone : `55${phone}`;
    let msg = `Olá ${p.name}! Sua consulta está agendada para ${new Date(apt.date + 'T12:00').toLocaleDateString('pt-BR')} às ${apt.time}.`;
    if (apt.meet_link) msg += `\nLink: ${apt.meet_link}`;
    window.open(`https://wa.me/${full}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const copyLink = () => {
    if (!apt.meet_link) return;
    navigator.clipboard.writeText(apt.meet_link);
    toast.success('Link copiado!');
  };

  const openProntuario = () => { window.location.href = `/doctor/evolution?patient=${apt.patient_id}`; };

  const deleteAppointment = async () => {
    setBusy(true);
    try {
      const res = await axios.delete(`${API_URL}/api/appointments/${apt.appointment_id}`, { withCredentials: true });
      const paid = res.data?.payments_deleted || 0;
      toast.success(paid > 0
        ? `Consulta excluída. ${paid} pagamento(s) removido(s).`
        : 'Consulta excluída permanentemente.'
      );
      setShowDeleteWarning(false);
      onUpdated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir consulta');
    }
    setBusy(false);
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="overflow-y-auto w-full sm:max-w-md" data-testid="apt-drawer">
        <SheetHeader>
          <SheetTitle className="text-2xl" style={{ fontFamily: 'Outfit, sans-serif' }}>Detalhes da Consulta</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Status badges row: separate consultation status from payment status */}
          <div className="flex flex-wrap gap-2">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold" style={{ backgroundColor: `${s.bg}15`, color: s.bg, border: `1px solid ${s.bg}40` }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.bg }} />
              Consulta: {s.label}
            </span>
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${isPaid ? 'bg-[#0F1B3D]/10 text-[#0F1B3D] border border-[#0F1B3D]/40' : 'bg-[#E9C46A]/15 text-[#8C6B1F] border border-[#E9C46A]/50'}`}>
              <CreditCard className="h-3 w-3" />
              Pagamento: {isPaid ? 'Pago' : 'Pendente'}
            </span>
          </div>

          {/* Quick actions based on current state */}
          <QuickActions
            apt={apt} isPaid={isPaid} busy={busy}
            approve={approve} reject={reject} markPaid={markPaid}
            setStatus={setStatus} sendWhatsApp={sendWhatsApp}
            generateMeet={generateMeet} copyLink={copyLink}
            openProntuario={openProntuario}
          />

          {/* Patient */}
          <Section title="Paciente" icon={User}>
            <Row icon={User} label="Nome">{p.name || '—'}</Row>
            <Row icon={Phone} label="Telefone">{p.phone || '—'}</Row>
            <Row icon={Mail} label="E-mail">{p.email || '—'}</Row>
            <Row icon={Cake} label="Nascimento">{p.birth_date || '—'}</Row>
          </Section>

          {/* Appointment */}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#7A85A0] font-semibold">
                <CalIcon className="h-3.5 w-3.5" /> Consulta
              </div>
              {!isLocked && !editing && (
                <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-7 text-xs text-[#0F1B3D]" data-testid="drawer-edit-btn">
                  <Edit className="h-3.5 w-3.5 mr-1" /> Editar
                </Button>
              )}
            </div>
            {editing ? (
              <div className="bg-[#FAF6EE] rounded-lg p-3 border border-[#E5DCC8] space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Data</Label>
                    <Input type="date" value={editForm.date} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="edit-date-input" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Horário</Label>
                    <Input type="time" value={editForm.time} onChange={(e) => setEditForm({ ...editForm, time: e.target.value })} data-testid="edit-time-input" />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={editForm.type} onValueChange={(val) => setEditForm({ ...editForm, type: val })}>
                    <SelectTrigger data-testid="edit-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {servicePrices.length > 0 ? servicePrices.map(sp => (
                        <SelectItem key={sp.service_type} value={sp.service_type}>{sp.label} - R$ {sp.price.toFixed(2)}</SelectItem>
                      )) : (
                        <>
                          <SelectItem value="clinica_geral">Clínica Geral</SelectItem>
                          <SelectItem value="tirzepatida">Tirzepatida</SelectItem>
                          <SelectItem value="retorno">Retorno</SelectItem>
                        </>
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 pt-1">
                  <Button size="sm" variant="outline" className="flex-1 border-[#E5DCC8]" onClick={() => setEditing(false)} disabled={busy} data-testid="edit-cancel-btn">
                    <X className="h-3.5 w-3.5 mr-1" /> Cancelar
                  </Button>
                  <Button size="sm" className="flex-1 bg-[#A8174E] hover:bg-[#8E1142] text-white" onClick={saveEdit} disabled={busy} data-testid="edit-save-btn">
                    <Save className="h-3.5 w-3.5 mr-1" /> Salvar
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-1.5">
                <Row icon={CalIcon} label="Data">{fullDate}</Row>
                <Row icon={Clock} label="Horário">{apt.time} · {apt.duration_minutes || 30} min</Row>
                <Row icon={ExternalLink} label="Tipo">{TYPE_LABEL[apt.type] || apt.type}</Row>
                {apt.notes && <Row icon={ExternalLink} label="Observações">{apt.notes}</Row>}
              </div>
            )}
          </div>

          {/* Videochamada */}
          <Section title="Videochamada" icon={Video}>
            {apt.meet_link ? (
              <div className="space-y-2">
                <div className="bg-[#FAF6EE] rounded-lg p-3 text-xs text-[#5F6B85] break-all border border-[#E5DCC8]">{apt.meet_link}</div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" className="flex-1 border-[#E5DCC8]" onClick={copyLink} data-testid="drawer-copy-meet">
                    <Copy className="h-4 w-4 mr-2" /> Copiar Link
                  </Button>
                  <Button size="sm" className="flex-1 bg-[#A8174E] hover:bg-[#8E1142] text-white" onClick={() => window.open(apt.meet_link, '_blank', 'noopener,noreferrer')} data-testid="drawer-join-meet">
                    <Video className="h-4 w-4 mr-2" /> Entrar
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-[#7A85A0]">O link do Meet é gerado automaticamente ao confirmar o pagamento.</p>
            )}
          </Section>

          {/* Danger Zone: Delete Appointment */}
          <div className="space-y-2 pt-4 border-t border-[#E5DCC8]">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#A8174E] font-semibold">
              <AlertTriangle className="h-3.5 w-3.5" /> Zona de risco
            </div>
            <p className="text-xs text-[#7A85A0]">
              Excluir remove permanentemente a consulta e o pagamento associado. Ação irreversível.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowDeleteWarning(true)}
              disabled={busy}
              className="w-full border-[#A8174E]/40 text-[#A8174E] hover:bg-[#A8174E]/5"
              data-testid="drawer-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Excluir consulta
            </Button>
          </div>
        </div>

        <AlertDialog open={showDeleteWarning} onOpenChange={(o) => { if (!o) { setShowDeleteWarning(false); setDeleteConfirmText(''); } }}>
          <AlertDialogContent data-testid="delete-apt-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2 text-[#A8174E]">
                <AlertTriangle className="h-5 w-5" /> Excluir consulta permanentemente?
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3 text-sm text-[#5F6B85]">
                  <p>
                    Esta ação é <strong className="text-[#A8174E]">irreversível</strong>. Serão removidos:
                  </p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>A consulta de <strong>{p.name || 'paciente'}</strong> em <strong>{fullDate} às {apt.time}</strong></li>
                    <li>O pagamento associado (se houver)</li>
                    <li>Notificações relacionadas</li>
                  </ul>
                  <p className="text-xs text-[#7A85A0]">
                    O evento no Google Calendar não é removido automaticamente — exclua-o manualmente se necessário.
                  </p>
                  <div className="pt-2">
                    <Label className="text-xs">Digite <strong>EXCLUIR</strong> para confirmar</Label>
                    <Input
                      value={deleteConfirmText}
                      onChange={(e) => setDeleteConfirmText(e.target.value)}
                      placeholder="EXCLUIR"
                      className="mt-1"
                      data-testid="delete-confirm-input"
                      autoFocus
                    />
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={busy} data-testid="delete-cancel-btn">Cancelar</AlertDialogCancel>
              <AlertDialogAction
                disabled={busy || deleteConfirmText.trim().toUpperCase() !== 'EXCLUIR'}
                onClick={(e) => { e.preventDefault(); deleteAppointment(); }}
                className="bg-[#A8174E] hover:bg-[#8E1142] text-white"
                data-testid="delete-confirm-btn"
              >
                {busy ? 'Excluindo...' : 'Excluir permanentemente'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SheetContent>
    </Sheet>
  );
};

const QuickActions = ({ apt, isPaid, busy, approve, reject, markPaid, setStatus, sendWhatsApp, generateMeet, copyLink, openProntuario }) => {
  const buttons = [];

  if (apt.status === 'pending_approval') {
    buttons.push(
      <Button key="a" size="sm" disabled={busy} onClick={approve} className="bg-[#0F1B3D] hover:bg-[#08122A] text-white" data-testid="drawer-approve">
        <Check className="h-4 w-4 mr-1" /> Aprovar
      </Button>,
      <Button key="r" size="sm" variant="outline" disabled={busy} onClick={reject} className="border-[#E5DCC8] text-[#A8174E]" data-testid="drawer-reject">
        <XCircle className="h-4 w-4 mr-1" /> Rejeitar
      </Button>
    );
  }

  if (apt.status === 'awaiting_payment' && !isPaid) {
    buttons.push(
      <Button key="mp" size="sm" disabled={busy} onClick={markPaid} className="bg-[#0F1B3D] hover:bg-[#08122A] text-white" data-testid="drawer-mark-paid">
        <CreditCard className="h-4 w-4 mr-1" /> Marcar como Pago
      </Button>
    );
  }

  if (['scheduled', 'rescheduled', 'awaiting_payment'].includes(apt.status)) {
    buttons.push(
      <Button key="wa" size="sm" variant="outline" disabled={busy} onClick={sendWhatsApp} className="border-[#E5DCC8]" data-testid="drawer-whatsapp">
        <MessageCircle className="h-4 w-4 mr-1" /> WhatsApp
      </Button>
    );
  }

  if (['scheduled', 'rescheduled'].includes(apt.status) && !apt.meet_link) {
    buttons.push(
      <Button key="cal" size="sm" variant="outline" disabled={busy} onClick={generateMeet} className="border-[#E5DCC8]" data-testid="drawer-gen-meet">
        <CalendarCheck className="h-4 w-4 mr-1" /> Gerar Meet
      </Button>
    );
  }

  if (['scheduled', 'rescheduled'].includes(apt.status)) {
    buttons.push(
      <Button key="cp" size="sm" variant="outline" disabled={busy} onClick={() => setStatus('completed')} className="border-[#E5DCC8]" data-testid="drawer-complete">
        <Check className="h-4 w-4 mr-1" /> Concluir
      </Button>,
      <Button key="cc" size="sm" variant="outline" disabled={busy} onClick={() => setStatus('cancelled')} className="border-[#E5DCC8] text-[#A8174E]" data-testid="drawer-cancel">
        <XCircle className="h-4 w-4 mr-1" /> Cancelar
      </Button>
    );
  }

  buttons.push(
    <Button key="pr" size="sm" variant="outline" onClick={openProntuario} className="border-[#E5DCC8]" data-testid="drawer-prontuario">
      <FileText className="h-4 w-4 mr-1" /> Prontuário
    </Button>
  );

  return (
    <div className="space-y-2">
      <div className="text-xs uppercase tracking-wider text-[#7A85A0] font-semibold flex items-center gap-2">
        <Edit className="h-3.5 w-3.5" /> Ações
      </div>
      <div className="flex flex-wrap gap-2">{buttons}</div>
    </div>
  );
};

const Section = ({ title, icon: Icon, children }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-[#7A85A0] font-semibold">
      <Icon className="h-3.5 w-3.5" /> {title}
    </div>
    <div className="space-y-1.5">{children}</div>
  </div>
);

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex items-start gap-3 text-sm">
    <Icon className="h-4 w-4 text-[#7A85A0] mt-0.5 shrink-0" />
    <div className="flex-1">
      <p className="text-xs text-[#7A85A0]">{label}</p>
      <p className="text-[#0F1B3D] font-medium break-words">{children}</p>
    </div>
  </div>
);
